import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import { CheckCircle, ArrowRight, Sparkle, Warning } from "@phosphor-icons/react";
import FarmersNav from "@/components/FarmersNav";
import { fpPath } from "@/lib/site";

/**
 * FarmersBillingSuccess — post-checkout landing page.
 *
 * Polls /api/billing/status/{session_id} until payment_status === "paid"
 * (or a hard timeout of 20 seconds). Webhook + inline fallback in the
 * status endpoint means the subscription is upserted before we return.
 */
export default function FarmersBillingSuccess() {
  const [params] = useSearchParams();
  const session_id = params.get("session_id");
  const [state, setState] = useState({ status: "polling", tries: 0 });

  useEffect(() => {
    if (!session_id) { setState({ status: "error", message: "Missing session" }); return; }
    let cancelled = false;
    let tries = 0;
    const tick = async () => {
      if (cancelled) return;
      tries += 1;
      try {
        const r = await http.get(`/billing/status/${session_id}`);
        const ps = r.data.payment_status;
        if (ps === "paid") { setState({ status: "paid" }); return; }
        if (ps === "expired" || ps === "failed") { setState({ status: "failed" }); return; }
      } catch (e) { /* transient, retry */ }
      if (tries >= 10) { setState({ status: "timeout" }); return; }
      setState({ status: "polling", tries });
      setTimeout(tick, 2000);
    };
    tick();
    return () => { cancelled = true; };
  }, [session_id]);

  return (
    <main className="min-h-screen pt-32 md:pt-36 pb-24 px-5 md:px-10 bg-[#F5EBDC]" data-testid="farmers-billing-success-page">
      <div className="max-w-6xl mx-auto"><FarmersNav /></div>
      <div className="max-w-2xl mx-auto mt-10 text-center">
        {state.status === "polling" && (
          <>
            <Sparkle size={40} weight="fill" className="mx-auto text-[color:var(--np-brand,#B85F2A)] animate-pulse" />
            <h1 className="mt-4 font-serif-display text-3xl">Confirming your subscription…</h1>
            <p className="mt-2 text-[#3A3830]">This usually takes a few seconds. Hang tight.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle size={48} weight="fill" className="mx-auto text-[#0f7a3e]" />
            <h1 className="mt-4 font-serif-display text-4xl">You're in.</h1>
            <p className="mt-2 text-[#3A3830]">Your Farmers Pulse subscription is live — every premium feature is unlocked.</p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
              <Link to={fpPath("/websites")}
                    data-testid="farmers-billing-success-shop"
                    className="rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase">
                Set up my shop <ArrowRight size={11} weight="bold" className="inline ml-1" />
              </Link>
              <Link to={fpPath("/billing")}
                    data-testid="farmers-billing-success-billing"
                    className="rounded-full border border-black/15 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:border-[color:var(--np-brand,#B85F2A)] hover:text-[color:var(--np-brand,#B85F2A)]">
                Manage plan
              </Link>
            </div>
          </>
        )}
        {(state.status === "failed" || state.status === "error") && (
          <>
            <Warning size={40} weight="fill" className="mx-auto text-[color:var(--np-brand,#B85F2A)]" />
            <h1 className="mt-4 font-serif-display text-3xl">Something went wrong</h1>
            <p className="mt-2 text-[#3A3830]">Your card wasn't charged. You can try again from the plans page.</p>
            <Link to={fpPath("/billing")}
                  className="mt-4 inline-flex rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase">
              Back to plans
            </Link>
          </>
        )}
        {state.status === "timeout" && (
          <>
            <Sparkle size={40} weight="fill" className="mx-auto text-[color:var(--np-brand,#B85F2A)]" />
            <h1 className="mt-4 font-serif-display text-3xl">Still processing…</h1>
            <p className="mt-2 text-[#3A3830]">Stripe is taking a little longer than usual. Refresh this page in a minute or open Manage plan.</p>
            <Link to={fpPath("/billing")}
                  className="mt-4 inline-flex rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase">
              Open my plan
            </Link>
          </>
        )}
      </div>
    </main>
  );
}
